import React, { useState } from "react";
import VisitDetailEventList from "./components/VisitDetailEventList";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { ChevronLeftIcon, ClockIcon, MapPinIcon, CalendarDaysIcon } from "@heroicons/react/16/solid";
import { PencilIcon, DocumentTextIcon, BanknotesIcon, ArrowTopRightOnSquareIcon, UserIcon } from "@heroicons/react/24/outline";
import { Link } from "../../design/link";
import { Divider } from "../../design/divider";
import { Avatar } from "../../design/avatar";
import VisitStatusBadge from "../../components/VisitStatusBadge";
import DeleteVisitButton from "./components/DeleteVisitButton";
import LoadingView from "../../components/LoadingView";
import moment from "moment";
import EditVisitButton from "./components/EditVisitButton";
import { Button } from "../../design/button";
import ScheduleVisitButton from "./components/ScheduleVisitButton";
import CancelVisitButton from "./components/CancelVisitButton";
import MarkNoShowButton from "./components/MarkNoShowButton";
import CreateReportButton from "./components/CreateReportButton";
import MarkAsPaidButton from "./components/MarkAsPaidButton";
import RequestPaymentButton from "./components/RequestPaymentButton";
import MarkMonitorPaidButton from "./components/MarkMonitorPaidButton";
import InvitationStatusBadge from "../../components/InvitationStatusBadge";
import { getInitials } from "../../utils/string";
import ErrorView from "../../components/ErrorView";
import { Badge } from "../../design/badge";
import {
  VisitHeaderProps,
  VisitParticipantsProps,
  VisitDetailsCardProps,
} from "./types";
import { useVisitDetail } from "./hooks/useVisitDetail";
import { useAuth } from "../../contexts/AuthContext";
import { useVisit } from "../../contexts/VisitContext";
import { Text } from "../../design/text";
import { TextEditDialog } from "../../design/text-edit-dialog";
import { Visit, NO_SHOW_PARTY_LABELS } from "@visitproof/types";
import { Timestamp } from "firebase/firestore";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useAccount } from "../../contexts/AccountContext";
import SignInSheetCard from "./components/SignInSheetCard";

// Breadcrumb Navigation
const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/visits"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Visits
    </Link>
  </div>
);

// Visit Header with Status Badge and Actions
const VisitHeader: React.FC<VisitHeaderProps> = React.memo(({
  visit,
  visitId,
  onDuplicateVisit,
  visitReport,
  loadingReport,
  duration,
}) => {
  const { isAdmin } = useAuth();

  // Determine if this visit has related data that would be affected by deletion
  const hasReports = Boolean(visit.reportId || visitReport);
  const hasEvents = Boolean(visit.eventReportPath);

  return (
    <div className="mt-4 lg:mt-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Heading>
            {moment(visit.startDate.toDate()).format("MMMM Do, YYYY")}
          </Heading>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <VisitStatusBadge status={visit.status} />
            <span className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
              <ClockIcon className="size-4" />
              {moment(visit.startDate.toDate()).format("h:mm A")} - {moment(visit.endDate.toDate()).format("h:mm A")}
            </span>
            <span className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
              <CalendarDaysIcon className="size-4" />
              {`${duration.hours()}h ${duration.minutes()}m`}
            </span>
            {visit.location && (
              <span className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
                <MapPinIcon className="size-4" />
                {visit.location.name}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Admin-only actions: Edit, Duplicate, Schedule, Cancel, Delete */}
          {isAdmin && (
            <>
              <EditVisitButton visit={visit} />
              <Button outline onClick={onDuplicateVisit}>
                Duplicate
              </Button>
              {visit.status === "pending" && visitId && (
                <ScheduleVisitButton visitId={visitId} />
              )}
              {visit.status === "scheduled" && (
                <>
                  <MarkNoShowButton visitId={visit.id} />
                  <CancelVisitButton visitId={visit.id} />
                </>
              )}
            </>
          )}
          {/* Create Report - available to both admins and supervisors */}
          {["supervisor_ended", "completed"].includes(visit.status) && !loadingReport && !visitReport && (
            <CreateReportButton visit={visit} />
          )}
          {/* View Report - available to both admins and supervisors */}
          {(visit.reportId || visitReport) && (
            <Button
              color="blue"
              href={`/report/${visit.reportId || visitReport?.id}`}
            >
              View Report
            </Button>
          )}
          {/* Delete button - admin only */}
          {isAdmin && (
            <DeleteVisitButton
              visitId={visit.id}
              hasEvents={hasEvents}
              hasReports={hasReports}
            />
          )}
        </div>
      </div>
    </div>
  );
});

// Visit Details Card
const VisitDetailsCard: React.FC<VisitDetailsCardProps> = React.memo(({ visit, caseInfo }) => (
  <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <Subheading>Visit Details</Subheading>
    <Divider className="my-4" />
    <DescriptionList>
      <DescriptionTerm>Case</DescriptionTerm>
      <DescriptionDetails>
        <Link
          href={`/case/${visit.caseId}`}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {caseInfo?.identifier ?? visit.caseId}
        </Link>
      </DescriptionDetails>

      <DescriptionTerm>Start</DescriptionTerm>
      <DescriptionDetails>
        {moment(visit.startDate.toDate()).format("MMMM Do, YYYY [at] h:mm A")}
      </DescriptionDetails>

      <DescriptionTerm>End</DescriptionTerm>
      <DescriptionDetails>
        {moment(visit.endDate.toDate()).format("MMMM Do, YYYY [at] h:mm A")}
      </DescriptionDetails>

      {visit.location && (
        <>
          <DescriptionTerm>Location</DescriptionTerm>
          <DescriptionDetails>{visit.location.name}</DescriptionDetails>
        </>
      )}

      {visit.notes && (
        <>
          <DescriptionTerm>Notes</DescriptionTerm>
          <DescriptionDetails className="whitespace-pre-wrap">
            {visit.notes}
          </DescriptionDetails>
        </>
      )}

      {visit.cancelationReason && (
        <>
          <DescriptionTerm>Cancellation Reason</DescriptionTerm>
          <DescriptionDetails>{visit.cancelationReason}</DescriptionDetails>
        </>
      )}
    </DescriptionList>
  </div>
));

// Post-Visit Notes Card
interface PostVisitNotesCardProps {
  visit: Visit;
  onSave: (notes: string) => Promise<void>;
}

const PostVisitNotesCard: React.FC<PostVisitNotesCardProps> = ({ visit, onSave }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Only show edit button for visits that have ended or completed
  const canAddNotes = ["supervisor_ended", "completed"].includes(visit.status);

  return (
    <>
      <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
            <Subheading>Post-Visit Notes</Subheading>
          </div>
          {canAddNotes && (
            <Button outline onClick={() => setIsDialogOpen(true)}>
              <PencilIcon className="h-4 w-4" />
              {visit.postVisitNotes ? "Edit" : "Add Notes"}
            </Button>
          )}
        </div>
        <Divider className="my-4" />

        {visit.postVisitNotes ? (
          <div>
            <Text className="whitespace-pre-wrap text-zinc-700 dark:text-zinc-300">
              {visit.postVisitNotes}
            </Text>
            {visit.postVisitNotesAddedAt && (
              <Text className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                Added {moment(visit.postVisitNotesAddedAt.toDate()).format("MMMM Do, YYYY [at] h:mm A")}
              </Text>
            )}
          </div>
        ) : canAddNotes ? (
          <Text className="text-zinc-500 dark:text-zinc-400 italic">
            No post-visit notes added yet. Click "Add Notes" to document any observations that occurred outside the scheduled visit time.
          </Text>
        ) : (
          <Text className="text-zinc-500 dark:text-zinc-400 italic">
            Post-visit notes can be added after the visit has ended.
          </Text>
        )}
      </div>

      <TextEditDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={onSave}
        title="Post-Visit Notes"
        description="Document any observations or events that occurred outside the scheduled visit time (e.g., as participants were arriving or leaving, follow-up items, etc.)"
        placeholder="Enter your notes here..."
        initialValue={visit.postVisitNotes || ""}
        saveLabel="Save Notes"
        rows={8}
      />
    </>
  );
};

// No-Show Details Card
interface NoShowDetailsCardProps {
  visit: Visit;
}

const NoShowDetailsCard: React.FC<NoShowDetailsCardProps> = ({ visit }) => {
  if (visit.status !== "no_show" || !visit.noShowDetails) {
    return null;
  }

  return (
    <div className="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <Subheading className="text-amber-800 dark:text-amber-200">No-Show Details</Subheading>
      </div>
      <Divider className="my-4 border-amber-200 dark:border-amber-700" />
      <DescriptionList>
        <DescriptionTerm>Who didn't show up</DescriptionTerm>
        <DescriptionDetails>
          <Badge color="amber">
            {NO_SHOW_PARTY_LABELS[visit.noShowDetails.noShowParty]}
          </Badge>
        </DescriptionDetails>

        {visit.noShowDetails.notes && (
          <>
            <DescriptionTerm>Notes</DescriptionTerm>
            <DescriptionDetails className="whitespace-pre-wrap">
              {visit.noShowDetails.notes}
            </DescriptionDetails>
          </>
        )}

        {visit.noShowDetails.recordedAt && (
          <>
            <DescriptionTerm>Recorded</DescriptionTerm>
            <DescriptionDetails>
              {moment(visit.noShowDetails.recordedAt.toDate()).format("MMMM Do, YYYY [at] h:mm A")}
            </DescriptionDetails>
          </>
        )}
      </DescriptionList>
    </div>
  );
};

// Payment Status Card
interface PaymentStatusCardProps {
  visit: Visit;
}

const PaymentStatusCard: React.FC<PaymentStatusCardProps> = ({ visit }) => {
  const { isAdmin } = useAuth();
  const isPaid = visit.paymentStatus === "paid";

  return (
    <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BanknotesIcon className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
          <Subheading>Payment Status</Subheading>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <RequestPaymentButton visit={visit} />
            <MarkAsPaidButton visit={visit} />
          </div>
        )}
      </div>
      <Divider className="my-4" />

      <div className="flex items-center gap-3">
        <Badge color={isPaid ? "green" : "zinc"} className="text-sm">
          {isPaid ? "Paid" : "Unpaid"}
        </Badge>
        {isPaid && visit.paymentDate && (
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            on {moment(visit.paymentDate.toDate()).format("MMMM Do, YYYY")}
          </Text>
        )}
      </div>

      {isPaid && (
        <div className="mt-4 space-y-2">
          {visit.paymentNotes && (
            <Text className="text-sm text-zinc-600 dark:text-zinc-400">
              {visit.paymentNotes}
            </Text>
          )}
          {visit.paymentFileUrl && visit.paymentFileName && (
            <a
              href={visit.paymentFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              {visit.paymentFileName}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Monitor Payment Status Card
interface MonitorPaymentStatusCardProps {
  visit: Visit;
}

const MonitorPaymentStatusCard: React.FC<MonitorPaymentStatusCardProps> = ({ visit }) => {
  const { isAdmin } = useAuth();
  const isPaid = visit.monitorPaymentStatus === "paid";

  // Only show if there's a supervisor assigned
  if (!visit.supervisor) {
    return null;
  }

  return (
    <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserIcon className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
          <Subheading>Monitor Payment</Subheading>
        </div>
        {isAdmin && <MarkMonitorPaidButton visit={visit} />}
      </div>
      <Divider className="my-4" />

      <div className="flex items-center gap-3">
        <Badge color={isPaid ? "green" : "zinc"} className="text-sm">
          {isPaid ? "Paid" : "Unpaid"}
        </Badge>
        {isPaid && visit.monitorPaymentDate && (
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            on {moment(visit.monitorPaymentDate.toDate()).format("MMMM Do, YYYY")}
          </Text>
        )}
      </div>

      {isPaid && visit.monitorPaymentNotes && (
        <div className="mt-4">
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            {visit.monitorPaymentNotes}
          </Text>
        </div>
      )}
    </div>
  );
};

// Helper to convert full name to initials (e.g., "John Smith" → "J.S.")
const toStaffInitials = (firstName?: string, lastName?: string): string => {
  return [firstName, lastName].filter(Boolean).map(n => `${n![0].toUpperCase()}.`).join('');
};

// Participants Card
const ParticipantsCard: React.FC<VisitParticipantsProps & { showStaffInitialsOnly?: boolean }> = React.memo(({ visit, showStaffInitialsOnly }) => {
  const supervisorDisplayName = visit.supervisor
    ? showStaffInitialsOnly
      ? toStaffInitials(visit.supervisor.firstName, visit.supervisor.lastName)
      : `${visit.supervisor.firstName} ${visit.supervisor.lastName}`
    : '';

  return (
  <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <Subheading>Participants</Subheading>
    <Divider className="my-4" />
    <DescriptionList>
      {visit.supervisor && (
        <>
          <DescriptionTerm>Monitor</DescriptionTerm>
          <DescriptionDetails>
            <Link
              href={`/supervisor/${visit.supervisor.id}`}
              className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Avatar
                src={visit.supervisor.avatarUrl}
                className="size-6"
                initials={getInitials(`${visit.supervisor.firstName} ${visit.supervisor.lastName}`)}
              />
              <span>{supervisorDisplayName}</span>
            </Link>
          </DescriptionDetails>
        </>
      )}

      <DescriptionTerm>Parents / Guardians</DescriptionTerm>
      <DescriptionDetails>
        {visit.parents && Object.entries(visit.parents).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(visit.parents).map(([key, value]) => (
              <div key={key}>
                <div className="flex items-center gap-2">
                  <Link
                    href={`/parent/${key}`}
                    className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Avatar
                      src={value.avatarUrl}
                      className="size-6"
                      initials={getInitials(`${value.firstName} ${value.lastName}`)}
                    />
                    <span>{`${value.firstName} ${value.lastName}`}</span>
                  </Link>
                  {visit.parentInvitations && visit.parentInvitations[key] && (
                    <InvitationStatusBadge
                      status={visit.parentInvitations[key].status}
                    />
                  )}
                  {visit.attendance?.parentIds?.includes(key) ? (
                    <Badge color="green">Present</Badge>
                  ) : visit.attendance ? (
                    <Badge color="red">Absent</Badge>
                  ) : null}
                </div>
                {visit.parentInvitations?.[key]?.responseMessage && (
                  <p className="ml-8 mt-1 text-sm text-zinc-500 dark:text-zinc-400 italic">
                    &ldquo;{visit.parentInvitations[key].responseMessage}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-zinc-500 dark:text-zinc-400">No parents assigned</span>
        )}
      </DescriptionDetails>

      {visit.children && Object.entries(visit.children).length > 0 && (
        <>
          <DescriptionTerm>Children</DescriptionTerm>
          <DescriptionDetails>
            <div className="space-y-2">
              {Object.entries(visit.children).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Link
                    href={`/child/${key}`}
                    className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Avatar
                      src={value.avatarUrl}
                      className="size-6"
                      initials={getInitials(`${value.firstName} ${value.lastName}`)}
                    />
                    <span>{`${value.firstName} ${value.lastName}`}</span>
                  </Link>
                  {visit.attendance?.childIds?.includes(key) ? (
                    <Badge color="green">Present</Badge>
                  ) : visit.attendance ? (
                    <Badge color="red">Absent</Badge>
                  ) : null}
                </div>
              ))}
            </div>
          </DescriptionDetails>
        </>
      )}
    </DescriptionList>
  </div>
  );
});

const VisitDetailPage: React.FC = () => {
  const {
    selectedVisit,
    loading,
    error,
    caseInfo,
    duration,
    visitId,
    visitReport,
    loadingReport,
    handleDuplicateVisit,
  } = useVisitDetail();
  const { updateVisit } = useVisit();
  const { user, isAdmin } = useAuth();
  const { account } = useAccount();
  const showStaffInitialsOnly = account?.reportSettings?.showStaffInitialsOnly ?? false;

  const handleSavePostVisitNotes = async (notes: string) => {
    if (!selectedVisit) return;
    await updateVisit(selectedVisit.id, {
      postVisitNotes: notes || undefined,
      postVisitNotesAddedAt: Timestamp.now(),
      postVisitNotesAddedBy: user?.uid,
    });
  };

  // Early returns for loading and error states
  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedVisit) {
    return <ErrorView error={new Error("Visit not found")} />;
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Visit Header with Status and Actions */}
      <VisitHeader
        visit={selectedVisit}
        visitId={visitId}
        onDuplicateVisit={handleDuplicateVisit}
        visitReport={visitReport}
        loadingReport={loadingReport}
        duration={duration}
      />

      {/* No-Show Details - shown prominently if visit was a no-show */}
      <NoShowDetailsCard visit={selectedVisit} />

      {/* Documentation In Progress Banner */}
      {selectedVisit.status === "supervisor_ended" && (
        <div className="p-4 border rounded-xl border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <div className="flex items-start gap-3">
            <DocumentTextIcon className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Documentation In Progress
              </h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                This visit has ended. The monitor can continue adding events and notes. Once documentation is complete, mark it as finished to generate the report.
              </p>
              {isAdmin && (
                <Button
                  color="green"
                  className="mt-3"
                  onClick={async () => {
                    await updateVisit(selectedVisit.id, { status: "completed" });
                  }}
                >
                  Complete Documentation
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Two-Column Layout: Left (main content) / Right (sidebar) */}
      <div className="grid gap-6 lg:grid-cols-[1fr,380px]">
        {/* Left Column */}
        <div className="space-y-6">
          <VisitDetailsCard visit={selectedVisit} caseInfo={caseInfo} />

          {/* Events Section */}
          <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
            <VisitDetailEventList
              visitId={selectedVisit.id}
              eventReportPath={selectedVisit.eventReportPath}
              visitStatus={selectedVisit.status}
            />
          </div>

          {/* Post-Visit Notes */}
          <PostVisitNotesCard
            visit={selectedVisit}
            onSave={handleSavePostVisitNotes}
          />
        </div>

        {/* Right Column (Sidebar) */}
        <div className="space-y-6">
          <ParticipantsCard visit={selectedVisit} showStaffInitialsOnly={showStaffInitialsOnly} />
          {selectedVisit.accountId && (
            <SignInSheetCard
              visitId={selectedVisit.id}
              accountId={selectedVisit.accountId}
            />
          )}
          <PaymentStatusCard visit={selectedVisit} />
          {selectedVisit.supervisor && (
            <MonitorPaymentStatusCard visit={selectedVisit} />
          )}

          {/* Court Report Button */}
          {["supervisor_ended", "completed"].includes(selectedVisit.status) && (
            <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
              <Button
                color="blue"
                className="w-full"
                href={selectedVisit.reportId ? `/report/${selectedVisit.reportId}` : undefined}
                onClick={selectedVisit.reportId ? undefined : () => {}}
              >
                <DocumentTextIcon className="h-4 w-4" />
                {selectedVisit.reportId ? "View Court Report" : "Generate Court Report"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitDetailPage;
